import {
  Download,
  Mail,
  X,
  CheckCircle2,
  XCircle,
  TriangleAlert,
  DoorOpen,
  Cpu,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";

import { useState } from "react";

import logoMemoLock from "../../../assets/logo_memolock.svg";
import logoMemoBlack from "../../../assets/Logo-Memo-Black.png";

import ReactApexChart from "react-apexcharts";
import { MONTHS, HARDWARE_DOOR_COUNT_MAP } from "../../constants.js";

// Convert the report HTML into an image with modern CSS color support.
import html2canvas from "html2canvas-pro";

// Generate and download the PDF file.
import jsPDF from "jspdf";

const MonthlyReportPdfPreview = ({
  shop,
  report,
  controllers,
  onClose,
  onPeriodChange,
  isLoading,
}) => {
  // Store the currently selected year.
  const [selectedYear, setSelectedYear] = useState(
    new Date(report?.currentMonth?.period?.startDate).getFullYear(),
  );

  // Store the currently selected month (1-12).
  const [selectedMonth, setSelectedMonth] = useState(
    new Date(report?.currentMonth?.period?.startDate).getMonth() + 1,
  );

  // Get today's date to prevent selecting future months.
  const today = new Date();

  /*
   * Return the month and year label.
   */
  const getMonthLabel = (startDate) => {
    if (!startDate) {
      return "Unknown";
    }

    const date = new Date(startDate);

    const monthNumber = date.getUTCMonth() + 1;
    const formattedMonth = String(monthNumber).padStart(2, "0");

    const monthLabel =
      MONTHS.find((month) => month.value === formattedMonth)?.label ??
      "Unknown";

    const year = date.getUTCFullYear();

    return `${monthLabel} ${year}`;
  };
  /*
   * Return the controller object from its identifier.
   */
  const getControllerById = (controllers, controllerId) => {
    return controllers.find((controller) => controller.id === controllerId);
  };

  /*
   * Count intrusion alarms for a controller.
   */
  const getIntrusionEvents = (controllerId) => {
    return (
      report?.currentMonth?.logs?.intrusionAlarms?.filter(
        (event) => event.controllerId === controllerId,
      ) ?? []
    );
  };

  /*
   * Count open door warnings for a controller.
   */
  const getOpenDoorEvents = (controllerId) => {
    return (
      report?.currentMonth?.logs?.openDoorWarnings?.filter(
        (event) => event.controllerId === controllerId,
      ) ?? []
    );
  };

  /*
   * Count how many events belong to each door.
   */
  const getDoorEventCounts = (events, totalDoors) => {
    const counts = Object.fromEntries(
      Array.from({ length: totalDoors }, (_, index) => [index + 1, 0]),
    );

    events.forEach((event) => {
      const doorNumber = Number(event?.byteParam2);

      // Ignore invalid door numbers.
      if (doorNumber >= 1 && doorNumber <= totalDoors) {
        counts[doorNumber] += 1;
      }
    });

    return counts;
  };

  /*
   * Return the number of doors based on the controller SSID.
   */
  const getControllerDoorCount = (controller) => {
    const hardwareValue = controller?.device_info?.hardware_v ?? 0;
    const totalDoors = HARDWARE_DOOR_COUNT_MAP[Number(hardwareValue)] ?? 1;
    return totalDoors;
  };

  /*
   * Collect all security events from the current month.
   */
  const currentMonthSecurityEvents = [
    ...(report?.currentMonth?.logs?.intrusionAlarms ?? []),
    ...(report?.currentMonth?.logs?.openDoorWarnings ?? []),
  ];

  /*
   * Count how many security events belong to each controller.
   */
  const controllerEventCounts = currentMonthSecurityEvents.reduce(
    (counts, event) => {
      const controllerId = event?.controllerId;

      // Ignore events without a controller identifier.
      if (!controllerId) {
        return counts;
      }

      counts[controllerId] = (counts[controllerId] ?? 0) + 1;

      return counts;
    },
    {},
  );

  // Keep only the five controllers with the highest number of events.

  const topControllers = Object.entries(controllerEventCounts)
    .sort(([, totalA], [, totalB]) => totalB - totalA)
    .slice(0, 5)
    .map(([controllerId]) => getControllerById(controllers, controllerId))
    .filter(Boolean);

  /*
   * Build the Top 5 keys based on the total number of transactions.
   */
  const keyTransactionCounts = [
    ...(report?.currentMonth?.logs?.validKeyTransactions ?? []).map(
      (transaction) => ({
        ...transaction,
        transactionType: "valid",
      }),
    ),

    ...(report?.currentMonth?.logs?.invalidKeyTransactions ?? []).map(
      (transaction) => ({
        ...transaction,
        transactionType: "invalid",
      }),
    ),
  ].reduce((counts, transaction) => {
    const keySerialNumber = String(transaction?.keySerialNumber ?? "");

    // Ignore transactions without a key serial number.
    if (!keySerialNumber) {
      return counts;
    }

    // Create the key entry when it does not exist yet.
    if (!counts[keySerialNumber]) {
      counts[keySerialNumber] = {
        keySerialNumber,
        valid: 0,
        invalid: 0,
        total: 0,
      };
    }

    // Count valid and invalid transactions separately.
    if (transaction.transactionType === "valid") {
      counts[keySerialNumber].valid += 1;
    }

    if (transaction.transactionType === "invalid") {
      counts[keySerialNumber].invalid += 1;
    }

    // Count the total number of transactions for this key.
    counts[keySerialNumber].total += 1;

    return counts;
  }, {});

  // Keep only the five keys with the highest total number of transactions.
  const topKeys = Object.values(keyTransactionCounts)
    .sort((keyA, keyB) => keyB.total - keyA.total)
    .slice(0, 5);

  /*
   * Build the chart series for the Top 5 keys.
   */
  const topKeysSeries = [
    {
      name: "Valid Transactions",
      data: topKeys.map((key) => key.valid),
    },
    {
      name: "Invalid Transactions",
      data: topKeys.map((key) => key.invalid),
    },
  ];

  /*
   * Configure the Top 5 keys chart.
   */
  const topKeysChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },

    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
      },
    },

    dataLabels: {
      enabled: true,
    },

    xaxis: {
      categories: topKeys.map((key) => key.keySerialNumber),
    },

    tooltip: {
      shared: true,
      intersect: false,
    },

    legend: {
      position: "top",
    },

    colors: ["#22c55e", "#ef4444"],
  };

  // Monthly comparison data
  const transactionComparisonData = [
    {
      month: getMonthLabel(report?.previousMonth?.period?.startDate),
      valid: report?.previousMonth?.summary?.validKeyTransactions ?? 0,
      invalid: report?.previousMonth?.summary?.invalidKeyTransactions ?? 0,
    },
    {
      month: getMonthLabel(report?.currentMonth?.period?.startDate),
      valid: report?.currentMonth?.summary?.validKeyTransactions ?? 0,
      invalid: report?.currentMonth?.summary?.invalidKeyTransactions ?? 0,
    },
  ];

  // Security events comparison data
  const securityEventsComparisonData = [
    {
      month: getMonthLabel(report?.previousMonth?.period?.startDate),
      intrusion: report?.previousMonth?.summary?.intrusionAlarms ?? 0,
      openDoor: report?.previousMonth?.summary?.openDoorWarnings ?? 0,
    },
    {
      month: getMonthLabel(report?.currentMonth?.period?.startDate),
      intrusion: report?.currentMonth?.summary?.intrusionAlarms ?? 0,
      openDoor: report?.currentMonth?.summary?.openDoorWarnings ?? 0,
    },
  ];

  const securityEventsSeries = [
    {
      name: "Intrusion Alarms",
      data: securityEventsComparisonData.map((item) => item.intrusion),
    },
    {
      name: "Open Door Warnings",
      data: securityEventsComparisonData.map((item) => item.openDoor),
    },
  ];

  const securityEventsChartOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },

    stroke: {
      curve: "smooth",
      width: 3,
    },

    markers: {
      size: 6,
    },

    dataLabels: {
      enabled: true,
      offsetY: -10,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },

    xaxis: {
      categories: securityEventsComparisonData.map((item) => item.month),
    },

    // Security Events
    colors: ["#991b1b", "#d97706"],
  };

  const transactionSeries = [
    {
      name: "Valid Key Transactions",
      data: transactionComparisonData.map((item) => item.valid),
    },
    {
      name: "Invalid Key Transactions",
      data: transactionComparisonData.map((item) => item.invalid),
    },
  ];

  const transactionChartOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },

    stroke: {
      curve: "smooth",
      width: 3,
    },

    markers: {
      size: 6,
    },

    dataLabels: {
      enabled: true,
      offsetY: -10,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },

    xaxis: {
      categories: transactionComparisonData.map((item) => item.month),
    },

    // Valid vs Invalid Transactions
    // colors: ["#0be35b", "#bd0606"],
    colors: ["#22c55e", "#ef4444"],
  };

  /**
   * Calculates the percentage change between two values.
   */
  const calculatePercentageChange = (current, previous) => {
    // No comparison is possible if the previous month has no data.
    if (previous === 0) {
      return null;
    }

    return ((current - previous) / previous) * 100;
  };

  /**
   * Returns the icon and color for a percentage change.
   */
  const getTrend = (current, previous) => {
    const percentage = calculatePercentageChange(current, previous);

    if (percentage === null) {
      return {
        icon: Minus,
        color: "text-stone-500",
        value: "N/A",
      };
    }

    if (percentage > 0) {
      return {
        icon: TrendingUp,
        color: "text-green-600",
        value: `+${percentage.toFixed(1)}%`,
      };
    }

    if (percentage < 0) {
      return {
        icon: TrendingDown,
        color: "text-red-600",
        value: `${percentage.toFixed(1)}%`,
      };
    }

    return {
      icon: Minus,
      color: "text-stone-500",
      value: "0%",
    };
  };

  // Store the previous month totals for the KPI cards.
  const previousValidKeyTransactions =
    report?.previousMonth?.summary?.validKeyTransactions ?? 0;

  const previousInvalidKeyTransactions =
    report?.previousMonth?.summary?.invalidKeyTransactions ?? 0;

  const previousIntrusionAlarms =
    report?.previousMonth?.summary?.intrusionAlarms ?? 0;

  const previousOpenDoorWarnings =
    report?.previousMonth?.summary?.openDoorWarnings ?? 0;

  const validTrend = getTrend(
    report?.currentMonth?.summary?.validKeyTransactions ?? 0,
    report?.previousMonth?.summary?.validKeyTransactions ?? 0,
  );

  const invalidTrend = getTrend(
    report?.currentMonth?.summary?.invalidKeyTransactions ?? 0,
    report?.previousMonth?.summary?.invalidKeyTransactions ?? 0,
  );

  const intrusionTrend = getTrend(
    report?.currentMonth?.summary?.intrusionAlarms ?? 0,
    report?.previousMonth?.summary?.intrusionAlarms ?? 0,
  );

  const openDoorTrend = getTrend(
    report?.currentMonth?.summary?.openDoorWarnings ?? 0,
    report?.previousMonth?.summary?.openDoorWarnings ?? 0,
  );

  /*
   * Export the monthly report preview as a PDF file.
   */
  const handleDownloadPdf = async () => {
    try {
      // Select the complete report content.
      const reportElement = document.querySelector(
        ".monthly-report-pdf-content",
      );

      // Stop the export when the report content cannot be found.
      if (!reportElement) {
        throw new Error("Monthly report content was not found.");
      }

      // Convert the report HTML into a high-resolution canvas.
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f5f5f4",
        logging: false,
      });

      // Convert the canvas into a PNG image.
      const imageData = canvas.toDataURL("image/png");

      // Create an A4 PDF in landscape orientation.
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Add a small margin around the report.
      // Add a small margin around the report.
      const margin = 5;

      const availableWidth = pdfWidth - margin * 2;
      const availableHeight = pdfHeight - margin * 2;

      // Calculate the scale needed to fit the full report on one page.
      const widthScale = availableWidth / canvas.width;
      const heightScale = availableHeight / canvas.height;
      const scale = Math.min(widthScale, heightScale);

      // Preserve the original report proportions.
      const imageWidth = canvas.width * scale;
      const imageHeight = canvas.height * scale;

      // Add the complete report to a single PDF page.
      pdf.addImage(imageData, "PNG", margin, margin, imageWidth, imageHeight);

      // Build a safe filename using the shop name.
      const safeShopName = String(shop?.name || "shop")
        .replace(/[^a-z0-9]/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();

      // Download the generated PDF.
      pdf.save(`monthly-security-report-${safeShopName || "shop"}.pdf`);
    } catch (error) {
      console.error("Unable to generate the monthly report PDF:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="h-[95vh] w-[95vw] max-w-[1600px] overflow-auto rounded-2xl bg-stone-100 shadow-2xl">
        <div className="flex items-center justify-between border-b border-stone-200 bg-white px-8 py-5">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">
              Monthly Security Report
            </h1>

            <p className="mt-1 text-sm text-stone-500">Dashboard Preview</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Year selector */}
            <select
              value={selectedYear}
              disabled={isLoading}
              onChange={(event) => {
                // Update the selected year.
                const year = Number(event.target.value);

                setSelectedYear(year);

                // Notify the parent component about the selected period.
                onPeriodChange?.({
                  year,
                  month: selectedMonth,
                });
              }}
              className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm"
            >
              {[
                today.getFullYear(),
                today.getFullYear() - 1,
                today.getFullYear() - 2,
              ].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            {/* Month selector */}
            <select
              value={selectedMonth}
              disabled={isLoading}
              onChange={(event) => {
                // Update the selected month.
                const month = Number(event.target.value);

                setSelectedMonth(month);

                // Notify the parent component about the selected period.
                onPeriodChange?.({
                  year: selectedYear,
                  month,
                });
              }}
              className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm"
            >
              {MONTHS.map((month) => (
                <option
                  key={month.value}
                  value={Number(month.value)}
                  disabled={
                    selectedYear === today.getFullYear() &&
                    Number(month.value) > today.getMonth() + 1
                  }
                >
                  {month.label}
                </option>
              ))}
            </select>

            <button
              onClick={handleDownloadPdf}
              className="flex items-center gap-2 rounded-xl border border-stone-800 bg-stone-100 px-5 py-2.5 font-semibold text-stone-800 transition hover:bg-stone-300"
            >
              <Download size={18} />
              Download PDF
            </button>

            <button
              onClick={onClose}
              className="flex h-11 w-14 items-center justify-center font-semibold text-stone-800 rounded-xl border border-stone-300 hover:bg-stone-100"
            >
              {/* <X size={18} /> */}
              <p>Close</p>
            </button>
          </div>
        </div>
        {/* Shop information */}
        <div className="monthly-report-pdf-content space-y-6 p-8">
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between bg-[#0B1F44] px-8 py-5">
              {/* Left side */}
              <div className="flex items-center gap-4">
                <img
                  src={logoMemoLock}
                  alt="MemoLock"
                  className="h-15 w-auto"
                />

                <div>
                  <h1 className="text-2xl font-bold tracking-wide text-white">
                    MEMO LOCK APP
                  </h1>

                  <p className="text-sm text-slate-300">Security Systems</p>
                </div>
              </div>

              {/* Right side */}
              <div className="text-right">
                <h2 className="text-2xl font-semibold text-white">
                  Monthly Security Report
                </h2>

                <p className="mt-1 text-lg text-slate-300">
                  {getMonthLabel(report?.currentMonth?.period?.startDate)}
                </p>
              </div>
            </div>

            {/* Existing shop content */}
            <div className="flex items-start justify-between p-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
                  Shop
                </p>

                <h2 className="mt-2 text-3xl font-bold text-stone-900">
                  {shop?.name ?? "Shop name"}
                </h2>

                <p className="mt-2 text-base text-stone-500">
                  {shop?.brand ?? "Brand"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-x-14 gap-y-5 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-400">
                    Project
                  </p>

                  <p className="mt-1 font-semibold text-stone-900">
                    {shop?.projectNumber ?? "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-400">
                    Country
                  </p>

                  <p className="mt-1 font-semibold text-stone-900">
                    {shop?.country ?? "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-400">
                    Customer Email
                  </p>

                  <p className="mt-1 font-semibold text-stone-900">
                    {shop?.user_email ?? "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-400">
                    Generated
                  </p>

                  <p className="mt-1 font-semibold text-stone-900">
                    {new Date().toLocaleDateString("en-GB")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Summary KPI cards */}
          <section className="grid grid-cols-5 gap-4">
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100">
                  <CheckCircle2 size={18} className="text-stone-600" />
                </div>

                <p className="text-sm font-semibold text-stone-700">
                  Valid Key Transactions
                </p>
              </div>

              <p className="mt-5 text-4xl font-bold text-stone-900">
                {report?.currentMonth?.summary?.validKeyTransactions ?? 0}
              </p>

              {/* Previous month comparison */}
              <div className="mt-3 flex items-center gap-1">
                <validTrend.icon size={16} className={validTrend.color} />

                <span className={`text-sm font-semibold ${validTrend.color}`}>
                  {validTrend.value}
                </span>

                <span className="text-xs font text-stone-500">
                  vs previous month ({previousValidKeyTransactions})
                </span>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100">
                  <XCircle size={18} className="text-stone-600" />
                </div>

                <p className="text-sm font-semibold text-stone-700">
                  Invalid Key Transactions
                </p>
              </div>

              <p className="mt-5 text-4xl font-bold text-stone-900">
                {report?.currentMonth?.summary?.invalidKeyTransactions ?? 0}
              </p>

              {/* Previous month comparison */}
              <div className="mt-3 flex items-center gap-1">
                <invalidTrend.icon size={16} className={invalidTrend.color} />

                <span className={`text-sm font-semibold ${invalidTrend.color}`}>
                  {invalidTrend.value}
                </span>

                <span className="text-xs text-stone-500">
                  vs previous month ({previousInvalidKeyTransactions})
                </span>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100">
                  <TriangleAlert size={18} className="text-stone-600" />
                </div>

                <p className="text-sm font-semibold text-stone-700">
                  Intrusion Alarms
                </p>
              </div>

              <p className="mt-5 text-4xl font-bold text-stone-900">
                {report?.currentMonth?.summary?.intrusionAlarms ?? 0}
              </p>

              {/* Previous month comparison */}
              <div className="mt-3 flex items-center gap-1">
                <intrusionTrend.icon
                  size={16}
                  className={intrusionTrend.color}
                />

                <span
                  className={`text-sm font-semibold ${intrusionTrend.color}`}
                >
                  {intrusionTrend.value}
                </span>

                <span className="text-xs text-stone-500">
                  vs previous month ({previousIntrusionAlarms})
                </span>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100">
                  <DoorOpen size={18} className="text-stone-600" />
                </div>

                <p className="text-sm font-semibold text-stone-700">
                  Open Door Warnings
                </p>
              </div>

              <p className="mt-5 text-4xl font-bold text-stone-900">
                {report?.currentMonth?.summary?.openDoorWarnings ?? 0}
              </p>

              {/* Previous month comparison */}
              <div className="mt-3 flex items-center gap-1">
                <openDoorTrend.icon size={16} className={openDoorTrend.color} />

                <span
                  className={`text-sm font-semibold ${openDoorTrend.color}`}
                >
                  {openDoorTrend.value}
                </span>

                <span className="text-xs text-stone-500">
                  vs previous month ({previousOpenDoorWarnings})
                </span>
              </div>
            </div>

            {/* Previous Controllers */}
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100">
                  <Cpu size={18} className="text-stone-600" />
                </div>

                <p className="text-sm font-semibold text-stone-700">
                  Total Controllers
                </p>
              </div>

              <p className="mt-5 text-4xl font-bold text-stone-900">
                {controllers?.length ?? 0}
              </p>
              {/* Previous month comparison */}
              {/* <div className="mt-3 flex items-center gap-1">
                <Minus size={16} className="text-stone-500" />

                <span className="text-sm font-semibold text-stone-500">0%</span>

                <span className="text-xs text-stone-500">
                  vs previous month
                </span>
              </div> */}
            </div>
          </section>

          {/* Monthly comparison charts */}
          <section className="grid grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">
                    Valid vs Invalid Transactions
                  </h3>

                  <p className="text-sm text-stone-500">
                    Current month compared with previous month
                  </p>
                </div>

                <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
                  Monthly Comparison
                </span>
              </div>

              <div className="h-[320px]">
                <ReactApexChart
                  options={transactionChartOptions}
                  series={transactionSeries}
                  type="line"
                  height={320}
                />
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">
                    Security Events
                  </h3>

                  <p className="text-sm text-stone-500">
                    Intrusion alarms and open door warnings
                  </p>
                </div>

                <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
                  Monthly Comparison
                </span>
              </div>

              <div className="h-[320px]">
                <ReactApexChart
                  options={securityEventsChartOptions}
                  series={securityEventsSeries}
                  type="line"
                  height={320}
                />
              </div>
            </div>
          </section>

          {/* Security analysis charts */}
          <section className="grid grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-stone-900">
                Top 5 Controllers
              </h3>

              <p className="mt-1 text-sm text-stone-500">
                Controllers with the highest number of security events this
                month
              </p>

              <div className="mt-6 overflow-hidden rounded-xl border border-stone-200">
                <table className="w-full">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Controller
                      </th>

                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Intrusion Alarms
                      </th>

                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Open Door Warnings
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {topControllers.map((controller) => (
                      <tr
                        key={controller.id}
                        className="border-t border-stone-200"
                      >
                        <td className="px-4 py-3">
                          {(() => {
                            // Get the controller name.
                            const ssid = controller?.device_info?.ssid ?? "";

                            // Keep only the model name.
                            const model = ssid.split("|")[0];

                            // Use only the last 8 characters of the MAC address.
                            const shortMac = controller.id.slice(-8);

                            return (
                              <div>
                                {/* Controller name. */}
                                <div className="text-xs font-semibold text-stone-900">
                                  {`${model}|${shortMac}`}
                                </div>

                                {/* Full MAC address. */}
                                <div className="mt-1 font-mono text-xs text-stone-500">
                                  {controller.id}
                                </div>
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-4 py-3 align-top">
                          {(() => {
                            // Get all intrusion events for this controller.
                            const events = getIntrusionEvents(controller.id);

                            // Get the number of doors supported by this controller.
                            const totalDoors =
                              getControllerDoorCount(controller);

                            // Count events for each door.
                            const doorCounts = getDoorEventCounts(
                              events,
                              totalDoors,
                            );

                            return (
                              <div className="flex items-start gap-4">
                                {/* Display the total number of events. */}
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-[10px] font-semibold text-stone-500">
                                    Total
                                  </span>

                                  <span className="flex h-8 w-10 items-center justify-center rounded border border-stone-300 bg-stone-50 text-xs font-semibold">
                                    {events.length}
                                  </span>
                                </div>

                                <div
                                  className="grid gap-2"
                                  style={{
                                    gridTemplateColumns: "repeat(4, 32px)",
                                  }}
                                >
                                  {Array.from(
                                    { length: totalDoors },
                                    (_, index) => index + 1,
                                  ).map((door) => (
                                    <div
                                      key={door}
                                      className="flex flex-col items-center gap-1"
                                    >
                                      {/* Display the door label. */}
                                      <span className="text-[10px] font-semibold text-stone-500">
                                        D{door}
                                      </span>

                                      {/* Display the number of events for this door. */}
                                      <div
                                        className={`flex h-8 w-8 items-center justify-center rounded border text-xs font-semibold ${
                                          doorCounts[door] > 0
                                            ? "border-red-300 bg-red-50"
                                            : "border-stone-300 bg-white"
                                        }`}
                                      >
                                        {doorCounts[door]}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-4 py-3 align-top">
                          {(() => {
                            // Get all open door events for this controller.
                            const events = getOpenDoorEvents(controller.id);

                            // Get the number of doors supported by this controller.
                            const totalDoors =
                              getControllerDoorCount(controller);

                            // Count events for each door.
                            const doorCounts = getDoorEventCounts(
                              events,
                              totalDoors,
                            );

                            return (
                              <div className="flex items-start gap-4">
                                {/* Display the total number of events. */}
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-[10px] font-semibold text-stone-500">
                                    Total
                                  </span>

                                  <span className="flex h-8 w-10 items-center justify-center rounded border border-stone-300 bg-stone-50 text-xs font-semibold">
                                    {events.length}
                                  </span>
                                </div>

                                <div
                                  className="grid gap-2"
                                  style={{
                                    // Display a maximum of four doors per row.
                                    gridTemplateColumns: "repeat(4, 32px)",
                                  }}
                                >
                                  {Array.from(
                                    { length: totalDoors },
                                    (_, index) => index + 1,
                                  ).map((door) => (
                                    <div
                                      key={door}
                                      className="flex flex-col items-center gap-1"
                                    >
                                      {/* Display the door label. */}
                                      <span className="text-[10px] font-semibold text-stone-500">
                                        D{door}
                                      </span>

                                      {/* Display the number of events for this door. */}
                                      <div
                                        className={`flex h-8 w-8 items-center justify-center rounded border text-xs font-semibold ${
                                          doorCounts[door] > 0
                                            ? "border-orange-300 bg-orange-50"
                                            : "border-stone-300 bg-white"
                                        }`}
                                      >
                                        {doorCounts[door]}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-stone-900">
                Top 5 Keys
              </h3>

              <p className="mt-1 text-sm text-stone-500">
                Keys with the highest number of transactions this month
              </p>

              <div className="mt-6 overflow-hidden rounded-xl border border-stone-200">
                <table className="w-full">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Key Serial Number
                      </th>

                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Valid
                      </th>

                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Invalid
                      </th>

                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {topKeys.map((key) => (
                      <tr
                        key={key.keySerialNumber}
                        className="border-t border-stone-200"
                      >
                        <td className="px-4 py-3 text-xs">
                          {key.keySerialNumber}
                        </td>

                        <td className="px-4 py-3 text-center font-semibold text-sm text-green-800">
                          {key.valid}
                        </td>

                        <td className="px-4 py-3 text-center font-semibold text-sm text-red-600">
                          {key.invalid}
                        </td>

                        <td className="px-4 py-3 text-center text-sm font-semibold">
                          {key.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          {/* Security analysis charts */}
          <section className="grid grid-cols-2 gap-6">
            {/* Keep your existing content here. */}
          </section>

          {/* Footer */}
          <footer className="flex items-center justify-between border-t border-stone-300 bg-stone-100 px-8 py-4">
            <p className="text-xs text-stone-600">
              ©{" "}
              {new Date(
                report?.currentMonth?.period?.startDate,
              ).getUTCFullYear()}{" "}
              MEMO International Ltd. All rights reserved.
            </p>

            <img
              src={logoMemoBlack}
              alt="MEMO International"
              className="h-8 w-auto"
            />

            <p className="text-[15px] font-semibold text-stone-600">
              www.memolocks.com
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReportPdfPreview;
